'use client';

import { IPagination } from '@sapientpro/sapientpro-data-models';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import styles from './Pagination.module.scss';

type Page = {
  pageOffset: number;
  value: string | number;
};

interface IPaginationProps {
  data: IPagination;
  handlePageChange: (offset: number) => void;
}

const PLACEHOLDER = {
  pageOffset: 0,
  value: '. . .',
};

const MAX_CLOSEST_COUNT = 3;

const Pagination = ({ data, handlePageChange }: IPaginationProps) => {
  const [pages, setPages] = useState<Page[]>([]);
  const currentPage = Math.ceil(data.offset / data.limit) + 1;
  const pagesCount = Math.ceil(data.total / data.limit);

  useEffect(() => {
    if (data) {
      const allPages = new Array(pagesCount).fill(0).map((item, index) => ({ pageOffset: index * data.limit, value: index + 1 }));
      setPages(allPages);
    }
  }, [data]);

  const isFarFromFirst = currentPage - 1 > MAX_CLOSEST_COUNT;
  const isFarFromLast = pages.length - currentPage > MAX_CLOSEST_COUNT;

  const displayedPages = pages.reduce((result, page) => {
    const prevDiff = !isFarFromLast ? MAX_CLOSEST_COUNT : 1;
    const nextDiff = !isFarFromFirst ? MAX_CLOSEST_COUNT : 1;

    const pageDiff = Math.abs(Number(page.value) - currentPage);
    const isNext = currentPage < +page.value;
    const isPrev = currentPage > +page.value;
    const isToShow = (isPrev && pageDiff <= prevDiff) || (isNext && pageDiff <= nextDiff);
    const canShowAllPages = (MAX_CLOSEST_COUNT * 2) >= pages.length;
    const showFarFromLastPlaceHolder = isFarFromLast && !canShowAllPages;

    if (page.value === 1) {
      result.push(page);
      if (isFarFromFirst) result.push({ ...PLACEHOLDER, pageOffset: -1 });
    } else if (page.value === pages.length) {
      if (showFarFromLastPlaceHolder) result.push({ ...PLACEHOLDER, pageOffset: -2 });
      result.push(page);
    } else if (page.value === currentPage) {
      result.push(page);
    } else if (isToShow) {
      result.push(page);
    }

    return result;
  }, [] as Page[]);

  if (pagesCount === 1) return null;

  return (
    <div className={styles.pagination}>
      {displayedPages.map((page) => (
        <button
          type='button'
          key={page.pageOffset}
          className={classNames(styles.pageButton, { [styles.active]: page.pageOffset === data.offset })}
          onClick={() => {
            if (page.pageOffset > -1) {
              handlePageChange(page.pageOffset);
            }
          }}
        >
          <span className={styles.pageNumber}>{page.value}</span>
        </button>
      ))}
    </div>
  );
};

export default Pagination;
